/**
 * Business Hours Admin Page
 * Configure workshop opening hours, timezone, and closed dates
 * All schedule settings in one place
 */

import { useState, useEffect } from 'react';
import { Clock, Save, AlertCircle, Loader2, Calendar, Trash2, Plus, Globe, Copy, Check, ExternalLink } from 'lucide-react';
import { Button, Input } from '../../components/ui';
import { useToast } from '../../contexts/ToastContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { pageStyles } from '../../lib/styles';

const DAYS = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' },
] as const;

type DayKey = (typeof DAYS)[number]['key'];

interface DayHours {
  open: string;
  close: string;
  closed?: boolean;
}

interface BusinessHoursData {
  timezone: string;
  schedule: Record<DayKey, DayHours>;
}

interface ClosedDate {
  date: string;
  reason?: string;
  recurring?: boolean;
}

const TIMEZONES = [
  { value: 'Europe/Madrid', label: 'España (Madrid)' },
  { value: 'Europe/London', label: 'Reino Unido (Londres)' },
  { value: 'Europe/Paris', label: 'Francia (París)' },
  { value: 'Europe/Berlin', label: 'Alemania (Berlín)' },
  { value: 'Europe/Rome', label: 'Italia (Roma)' },
  { value: 'Europe/Lisbon', label: 'Portugal (Lisboa)' },
  { value: 'Atlantic/Canary', label: 'España (Canarias)' },
  { value: 'America/New_York', label: 'EEUU (Nueva York)' },
  { value: 'America/Los_Angeles', label: 'EEUU (Los Ángeles)' },
  { value: 'America/Mexico_City', label: 'México (Ciudad de México)' },
  { value: 'America/Buenos_Aires', label: 'Argentina (Buenos Aires)' },
  { value: 'America/Sao_Paulo', label: 'Brasil (São Paulo)' },
];

const DEFAULT_SCHEDULE: Record<DayKey, DayHours> = {
  monday: { open: '09:00', close: '18:00' },
  tuesday: { open: '09:00', close: '18:00' },
  wednesday: { open: '09:00', close: '18:00' },
  thursday: { open: '09:00', close: '18:00' },
  friday: { open: '09:00', close: '18:00' },
  saturday: { open: '09:00', close: '14:00' },
  sunday: { open: '09:00', close: '14:00', closed: true },
};

const DEFAULT_HOURS: BusinessHoursData = {
  timezone: 'Europe/Madrid',
  schedule: DEFAULT_SCHEDULE,
};

type TabType = 'schedule' | 'closedDates';

export function BusinessHoursPage() {
  const { success: showSuccess, error: showError } = useToast();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<TabType>('schedule');
  const [hours, setHours] = useState<BusinessHoursData>(DEFAULT_HOURS);
  const [closedDates, setClosedDates] = useState<ClosedDate[]>([]);
  const [hasHoursChanges, setHasHoursChanges] = useState(false);
  const [hasClosedDatesChanges, setHasClosedDatesChanges] = useState(false);

  // New closed date form
  const [newDate, setNewDate] = useState('');
  const [newReason, setNewReason] = useState('');
  const [newRecurring, setNewRecurring] = useState(false);
  const [copied, setCopied] = useState(false);

  // Public API URL
  const publicApiUrl = `${window.location.origin.replace('crm.', 'api.')}/api/public/hours`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(publicApiUrl);
    setCopied(true);
    showSuccess('URL copiada al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  // Fetch business hours
  const hoursQuery = useQuery({
    queryKey: ['admin', 'settings', 'businessHours'],
    queryFn: () =>
      api.get<{ key: string; value: BusinessHoursData | Record<DayKey, DayHours>; isDefault?: boolean }>(
        '/api/admin/settings/businessHours'
      ),
  });

  // Fetch closed dates
  const closedDatesQuery = useQuery({
    queryKey: ['admin', 'settings', 'closedDates'],
    queryFn: () =>
      api.get<{ key: string; value: ClosedDate[]; isDefault?: boolean }>('/api/admin/settings/closedDates'),
  });

  // Save business hours
  const saveHoursMutation = useMutation({
    mutationFn: (value: BusinessHoursData) =>
      api.put<{ key: string; value: BusinessHoursData }>('/api/admin/settings/businessHours', { value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'businessHours'] });
      setHasHoursChanges(false);
      showSuccess('Horarios guardados correctamente');
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Error al guardar');
    },
  });

  // Save closed dates
  const saveClosedDatesMutation = useMutation({
    mutationFn: (value: ClosedDate[]) =>
      api.put<{ key: string; value: ClosedDate[] }>('/api/admin/settings/closedDates', { value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'closedDates'] });
      setHasClosedDatesChanges(false);
      showSuccess('Fechas cerradas guardadas correctamente');
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Error al guardar');
    },
  });

  // Load hours data - handle both old format (just schedule) and new format (with timezone)
  useEffect(() => {
    if (hoursQuery.data?.value) {
      const val = hoursQuery.data.value;
      // Check if it's old format (no timezone property, just day keys)
      if ('monday' in val && !('timezone' in val)) {
        setHours({
          timezone: 'Europe/Madrid',
          schedule: val as Record<DayKey, DayHours>,
        });
      } else if ('schedule' in val && val.schedule) {
        // New format with timezone and schedule
        setHours(val as BusinessHoursData);
      } else {
        // Unknown format - use defaults
        console.warn('Unknown businessHours format, using defaults:', val);
        setHours(DEFAULT_HOURS);
      }
    }
  }, [hoursQuery.data]);

  // Load closed dates
  useEffect(() => {
    if (closedDatesQuery.data?.value && Array.isArray(closedDatesQuery.data.value)) {
      setClosedDates(closedDatesQuery.data.value);
    }
  }, [closedDatesQuery.data]);

  const updateTimezone = (timezone: string) => {
    setHours((prev) => ({ ...prev, timezone }));
    setHasHoursChanges(true);
  };

  const updateDay = (day: DayKey, field: keyof DayHours, value: string | boolean) => {
    setHours((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          [field]: value,
        },
      },
    }));
    setHasHoursChanges(true);
  };

  const handleSaveHours = () => {
    saveHoursMutation.mutate(hours);
  };

  const addClosedDate = () => {
    if (!newDate) return;

    if (closedDates.some((cd) => cd.date === newDate)) {
      showError('Esta fecha ya existe');
      return;
    }

    setClosedDates((prev) =>
      [
        ...prev,
        { date: newDate, reason: newReason || undefined, recurring: newRecurring || undefined },
      ].sort((a, b) => a.date.localeCompare(b.date))
    );
    setNewDate('');
    setNewReason('');
    setNewRecurring(false);
    setHasClosedDatesChanges(true);
  };

  const removeClosedDate = (date: string) => {
    setClosedDates((prev) => prev.filter((cd) => cd.date !== date));
    setHasClosedDatesChanges(true);
  };

  const handleSaveClosedDates = () => {
    saveClosedDatesMutation.mutate(closedDates);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Categorize closed dates
  const today = new Date().toISOString().split('T')[0];
  const upcomingDates = closedDates.filter((cd) => cd.date >= today && !cd.recurring);
  const pastDates = closedDates.filter((cd) => cd.date < today && !cd.recurring);
  const recurringDates = closedDates.filter((cd) => cd.recurring);

  const isLoading = hoursQuery.isLoading || closedDatesQuery.isLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={pageStyles.header}>
        <div>
          <h1 className={pageStyles.title}>Horarios y Calendario</h1>
          <p className={pageStyles.subtitle}>
            Horarios de apertura y festivos
          </p>
        </div>
      </div>

      {(hoursQuery.isError || closedDatesQuery.isError) && (
        <div className="rounded bg-red-50 px-3 py-2 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-red-800">Error al cargar</p>
            <p className="text-xs text-red-700">
              {(hoursQuery.error as Error)?.message || (closedDatesQuery.error as Error)?.message}
            </p>
          </div>
        </div>
      )}

      {/* Public API URL */}
      <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Globe className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-700">API Pública</p>
              <p className="text-[10px] text-gray-500 truncate">{publicApiUrl}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={copyToClipboard}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
            <a
              href={publicApiUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded hover:bg-gray-100 text-blue-600"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6">
          <button
            onClick={() => setActiveTab('schedule')}
            className={`py-1.5 px-0.5 border-b-2 text-xs font-medium ${
              activeTab === 'schedule'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Clock className="h-3 w-3 inline-block mr-1" />
            Horario
          </button>
          <button
            onClick={() => setActiveTab('closedDates')}
            className={`py-1.5 px-0.5 border-b-2 text-xs font-medium ${
              activeTab === 'closedDates'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Calendar className="h-3 w-3 inline-block mr-1" />
            Festivos
            {closedDates.length > 0 && (
              <span className="ml-1 bg-gray-100 text-gray-600 py-0.5 px-1.5 rounded-full text-[10px]">
                {closedDates.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Schedule Tab */}
      {activeTab === 'schedule' && (
        <div className="space-y-3">
          {/* Timezone selector */}
          <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
            <div className="flex items-center gap-3">
              <Globe className="h-3.5 w-3.5 text-gray-400" />
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-700">Zona Horaria</label>
                <select
                  value={hours.timezone}
                  onChange={(e) => updateTimezone(e.target.value)}
                  className="mt-1 w-full max-w-xs rounded border border-gray-300 px-2 py-1 text-xs bg-white"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Weekly schedule */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
              <h2 className="text-xs font-medium text-gray-900 uppercase tracking-wide">Horario Semanal</h2>
              <Button
                size="sm"
                onClick={handleSaveHours}
                disabled={!hasHoursChanges || saveHoursMutation.isPending}
              >
                {saveHoursMutation.isPending ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Save className="h-3 w-3 mr-1" />
                )}
                Guardar
              </Button>
            </div>

            <div className="divide-y divide-gray-100">
              {DAYS.map(({ key, label }) => {
                const dayHours = hours.schedule?.[key] || DEFAULT_SCHEDULE[key];
                return (
                  <div
                    key={key}
                    className={`flex items-center gap-3 px-3 py-2 ${
                      dayHours?.closed ? 'bg-gray-50/50' : ''
                    }`}
                  >
                    <div className="w-20">
                      <span className="text-xs font-medium text-gray-900">{label}</span>
                    </div>

                    <label className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={!dayHours?.closed}
                        onChange={(e) => updateDay(key, 'closed', !e.target.checked)}
                        className="h-3.5 w-3.5"
                      />
                      <span className="text-[10px] text-gray-600">Abierto</span>
                    </label>

                    {!dayHours?.closed && (
                      <>
                        <Input
                          type="time"
                          value={dayHours?.open || '09:00'}
                          onChange={(e) => updateDay(key, 'open', e.target.value)}
                          className="w-24 h-7 text-xs"
                        />
                        <span className="text-gray-400 text-xs">-</span>
                        <Input
                          type="time"
                          value={dayHours?.close || '18:00'}
                          onChange={(e) => updateDay(key, 'close', e.target.value)}
                          className="w-24 h-7 text-xs"
                        />
                      </>
                    )}

                    {dayHours?.closed && (
                      <span className="text-[10px] text-gray-400 italic">Cerrado</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Closed Dates Tab */}
      {activeTab === 'closedDates' && (
        <div className="space-y-3">
          {/* Add new date form */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
              <h2 className="text-xs font-medium text-gray-900 uppercase tracking-wide">Agregar Cierre</h2>
              <Button
                size="sm"
                onClick={handleSaveClosedDates}
                disabled={!hasClosedDatesChanges || saveClosedDatesMutation.isPending}
              >
                {saveClosedDatesMutation.isPending ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Save className="h-3 w-3 mr-1" />
                )}
                Guardar
              </Button>
            </div>

            <div className="p-3 flex flex-wrap gap-3 items-end">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Fecha</label>
                <Input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="w-36 h-8 text-sm"
                />
              </div>

              <div className="flex-1 min-w-[150px] space-y-1">
                <label className="text-xs font-medium text-gray-700">Motivo</label>
                <Input
                  type="text"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  placeholder="Navidad, Festivo..."
                  className="h-8 text-sm"
                />
              </div>

              <label className="flex items-center gap-1.5 pb-1">
                <input
                  type="checkbox"
                  checked={newRecurring}
                  onChange={(e) => setNewRecurring(e.target.checked)}
                  className="h-3.5 w-3.5"
                />
                <span className="text-xs text-gray-600">Anual</span>
              </label>

              <Button size="sm" onClick={addClosedDate} disabled={!newDate}>
                <Plus className="h-3 w-3 mr-1" />
                Añadir
              </Button>
            </div>
          </div>

          {/* Recurring dates */}
          {recurringDates.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-3 py-2 border-b border-gray-100">
                <h2 className="text-xs font-medium text-gray-900">
                  Festivos Anuales ({recurringDates.length})
                </h2>
              </div>
              <div className="divide-y divide-gray-100">
                {recurringDates.map((cd) => (
                  <div
                    key={cd.date}
                    className="flex items-center justify-between px-3 py-2 bg-purple-50/50"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-purple-500" />
                      <div>
                        <p className="text-xs font-medium text-gray-900">
                          {formatDate(cd.date).replace(/\d{4}$/, '(Anual)')}
                        </p>
                        {cd.reason && <p className="text-[10px] text-gray-500">{cd.reason}</p>}
                      </div>
                    </div>
                    <button
                      onClick={() => removeClosedDate(cd.date)}
                      className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming dates */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-3 py-2 border-b border-gray-100">
              <h2 className="text-xs font-medium text-gray-900">
                Próximos Cierres ({upcomingDates.length})
              </h2>
            </div>

            {upcomingDates.length === 0 ? (
              <p className="text-xs text-gray-500 italic p-3">No hay cierres programados.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {upcomingDates.map((cd) => (
                  <div
                    key={cd.date}
                    className="flex items-center justify-between px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      <div>
                        <p className="text-xs font-medium text-gray-900">{formatDate(cd.date)}</p>
                        {cd.reason && <p className="text-[10px] text-gray-500">{cd.reason}</p>}
                      </div>
                    </div>
                    <button
                      onClick={() => removeClosedDate(cd.date)}
                      className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Past dates */}
          {pastDates.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 opacity-60">
              <div className="px-3 py-2 border-b border-gray-100">
                <h2 className="text-xs font-medium text-gray-900">
                  Fechas Pasadas ({pastDates.length})
                </h2>
              </div>
              <div className="divide-y divide-gray-100">
                {pastDates.map((cd) => (
                  <div
                    key={cd.date}
                    className="flex items-center justify-between px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      <div>
                        <p className="text-xs font-medium text-gray-900">{formatDate(cd.date)}</p>
                        {cd.reason && <p className="text-[10px] text-gray-500">{cd.reason}</p>}
                      </div>
                    </div>
                    <button
                      onClick={() => removeClosedDate(cd.date)}
                      className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info box */}
      <div className="px-3 py-2 bg-blue-50 rounded-lg">
        <div className="flex items-start gap-2">
          <Clock className="h-3.5 w-3.5 text-blue-500 mt-0.5" />
          <p className="text-[10px] text-blue-700">
            Los horarios y festivos se usan para calcular disponibilidad en el widget de reservas.
          </p>
        </div>
      </div>
    </div>
  );
}
